// Mini-toolkit content for each archetype.
// Each is a 2-page printable companion delivered via Flodesk on email confirmation.
// Voice: warm, conversational, midwife-bestie. Positive and affirming throughout.
// American English throughout.

export const RESOURCES = {
  visionary: {
    title: "A Visionary's Birth Preparation Ritual",
    subtitle: "Three practices for the woman who dreams her life into being.",
    intro:
      "You came to this quiz already knowing. You've a vision of how this birth wants to unfold. All you need now is the space to trust it. Return to this short ritual throughout your pregnancy. It'll help you honor your inner knowing, hone your vision, and carry it lightly as birth approaches.",
    sections: [
      {
        label: 'One. Clarify your vision',
        body:
          "Find a quiet 20 minutes. Close your eyes. Just notice. When you picture the birth of your baby going beautifully, what do you see?",
        prompts: [
          "Where are you? Describe the space, the light, the sounds.",
          "Who's with you? How are they being with you?",
          "What's your body doing? What's your breath like?",
        ],
        note: "Write what comes through without editing. Keep this page near your bed for the rest of pregnancy; add to it as the vision continues to expand.",
      },
      {
        label: 'Two. A continuous breath practice',
        body:
          "Use this three times a day as your anchor: once upon waking, once before sleep, and at any other point in the day when you want to return to yourself. Let your breath flow freely and gently in and out, no holds between breaths.",
        prompts: [
          "Breathe in softly through your nose, counting to four. Silently say: \"My body already knows.\"",
          "Breathe out slowly through your mouth, counting to six. Silently say: \"My only job is to get out of its way.\"",
          "Continue this rhythm for five rounds, letting each breath soften the next.",
        ],
        note: "Just 5 breath cycles, about a minute total.",
      },
      {
        label: 'Three. Holding your vision with soft hands',
        body:
          "Our visions want to be held, not gripped. This weekly practice is a reminder of that. Sit with one sentence:",
        prompts: [
          "\"However this birth unfolds, I can still meet it with my full self.\"",
        ],
        note: "Let the sentence settle in for a few breaths. That softening is the space your vision needs to come to life.",
      },
    ],
    closing: "Your vision is your preparation. Carry it lightly, and let it carry you.",
  },

  warrior: {
    title: "A Warrior's Birth Training Plan",
    subtitle: "Embodiment tools for the woman who shows up prepared.",
    intro:
      "You don't hope. You prepare. This plan gives you three embodied practices to return to throughout pregnancy, so your body feels familiar with the landscape by the time labor begins.",
    sections: [
      {
        label: 'One. Daily breath training',
        body:
          "Practice for five minutes, once a day. It might feel too simple, but don't be fooled. The repetition now makes it automatic in labor.",
        prompts: [
          "Lie on your side. Place one hand on your belly.",
          "Breathe in through your nose for a count of four, letting your belly rise softly into your hand.",
          "Breathe out through your mouth for a count of eight, with a soft jaw and soft shoulders.",
          "Do eight gentle rounds. You're teaching your body to exhale longer than it inhales. That's the labor pattern.",
        ],
        note: "Keep practicing, especially on days you're tempted to skip.",
      },
      {
        label: 'Two. Your movement reference',
        body:
          "Your body will want to move in labor. Getting familiar with four positions now means they'll feel like home later.",
        prompts: [
          "Hands and knees with gentle hip rocks. Try 2 minutes.",
          "Deep supported squat, holding onto a doorframe. Try 1 minute.",
          "Standing sway, arms looped around your partner. Try 2 minutes.",
          "Side-lying with a pillow between your knees. Try 3 minutes.",
        ],
        note: "Repeat this practice weekly. Notice which positions your body favors, and share that with your support people. Understand your preferences may change in labor. This is to give you some practice in positions that commonly feel good to laboring women.",
      },
      {
        label: 'Three. Your Plan B',
        body:
          "Knowing you've thought it helps you move through birth open and unafraid.",
        prompts: [
          "If my labor needs time, my preferences are _____.",
          "If I choose pharmacological pain relief, my order of preference is ________.",
          "If a cesarean becomes the path, what's most important to me to have in the room is/are: _____.",
          "The phrase I want spoken to me in the hardest moment is _______.",
        ],
        note: "Share this page with your support team and care provider so everyone is on the same page.",
      },
    ],
    closing: "When birth asks everything of you, you'll lean into your preparation to have your back.",
  },

  scholar: {
    title: "A Scholar's Evidence-Based Questions",
    subtitle: "Questions worth asking your care provider before birth.",
    intro:
      "You deserve real answers. This list is your companion for appointments. Use it whenever a provider says 'we'll just do X,' so you can understand the why, the evidence, and whether it's the right choice for you.",
    sections: [
      {
        label: 'One. The BRAIN framework',
        body:
          "For any decision in pregnancy or birth (or with your newborn/child!), walk through these six questions before you say yes or no. Print this and keep it in your bag. Better yet, take a screenshot of it. Your partner can make it their phone wallpaper for labor.",
        prompts: [
          "B. Benefits. What are the specific benefits of this for me and my baby right now?",
          "R. Risks. What are the risks of proceeding, and how likely are they?",
          "A. Alternatives. What are my other options, including watching and waiting?",
          "I. Intuition. What does my gut say? How does my body feel about this?",
          "N. Nothing. What happens if we do nothing, or wait one hour?",
          "Plus. Evidence. What does the current evidence say, and which guideline are you citing?",
        ],
        note: "Any practitioner worth their salt will welcome these questions.",
      },
      {
        label: 'Two. Questions to ask about routine interventions',
        body: "Before you agree to any routine intervention, ask each question:",
        prompts: [
          "Is this routine for everyone here, or is there a specific clinical reason you're recommending it for me today?",
          "What's the evidence for this specific intervention at this gestation?",
          "What would we learn from it that could change our course of action?",
          "If I decline, what are the alternatives to monitor the same question?",
        ],
        note: "Routine isn't the same as necessary. These questions gently separate the two.",
      },
      {
        label: 'Three. Your research library',
        body: "Two resources worth your time this pregnancy:",
        prompts: [
          "Evidence Based Birth (evidencebasedbirth.com)",
          "Dr. Aviva Romm (avivaromm.com). Dr. Aviva offers a unique perspective merging her three areas of expertise as a midwife, herbalist, and Yale-trained women's health physician.",
        ],
        note: "More isn't always more. The trick for you is striking a balance on being <em>sufficiently</em> informed (so you have the information you need, but not so much that you can only live in your head about it.) Read less, read deeper.",
      },
    ],
    closing: "Information is medicine for you. Deepening your preparation comes from giving space to let your knowledge settle, and trust that your body has its own wisdom.",
  },

  feeler: {
    title: "A Feeler's Birth Environment Audit",
    subtitle: "Because who's in the room matters more than technique.",
    intro:
      "Your birth will be shaped by how you feel throughout the experience. This audit helps you intentionally choose what and who gets to be there with you.",
    sections: [
      {
        label: 'One. Your inner circle',
        body: "List the people who will be at your birth, or who you're considering. For each, answer the three questions (with your heart, not your head).",
        prompts: [
          "Would I feel comfortable pooping in front of this person?",
          "If I made unexpected or unusual sounds or movements, would they stay calm and supportive?",
          "Do I trust them to support my advocating for my preferences?",
        ],
        note: "If you feel a 'yes' to all three, they make the cut. <em>You're allowed to be selective.</em>",
      },
      {
        label: 'Two. The sensory support',
        body: "Birth is primal. Your senses are a superpower.",
        prompts: [
          "Sound. What playlist calms you down? Energizes you? Makes you feel like <em>you</em>? Make these now and check in with them at 37-38 weeks.",
          "Light. What's the dimmest light you can still see by? Think: Twinkly battery lights, a portable baby night light. Add that in your birth bag.",
          "Smell. Pick one essential oil that helps you feel calm. Lavender or anything woodsy is a safe choice. <em>Pro midwife/doula tip</em>: When you're ready to use this scent in labor, put a few drops on a washcloth. That way, when you need a break, the washcloth can be quickly removed (not so easily done if you put the oil directly on your skin.)",
          "Touch. Practice with your support person. A hand cupping the top outside of each arm, just below the top of your shoulder, counter-pressure on your hips, a hand squeeze. This may change in labor, but reviewing these now can provide helpful insight into your preferences.",
        ],
        note: "Rehearse the sensory setup in pregnancy. Your body will remember it in labor.",
      },
      {
        label: 'Three. Affirmations to keep close',
        body: "Write these on index cards. Have them near you in labor.",
        prompts: [
          "I'm held and surrounded by love.",
          "My heart knows the way.",
          "All of me belongs here.",
          "I feel my way through.",
        ],
        note: "Make up your own.",
      },
    ],
    closing: "Your emotional depth is exactly what birth and motherhood ask for.",
  },

  oracle: {
    title: "An Oracle's Question Journal",
    subtitle: "Prompts to think aloud with, for the woman who taps her wisdom through vocalizing.",
    intro:
      "You already know. You just need the right questions to let it take shape. This journal gives you six prompts to move through across pregnancy. Speak them aloud to a trusted person, voice-memo to yourself, or journal.",
    sections: [
      {
        label: 'One. What you already know',
        body: "Before you research anything new, sit with these four prompts.",
        prompts: [
          "What do I already know about how my body wants to do this?",
          "What have I heard myself think or say that I trusted immediately?",
          "Where has my intuition been right before, and what did I learn from following it?",
          "If I were advising a friend in my situation, what would I tell her?",
        ],
        note: "Come back to this in your last trimester. The answers will likely have shifted, and you'll see how much you've grown.",
      },
      {
        label: "Two. The 'But why?' questions",
        body: "For anyone advising you on your birth (your mother, a well-meaning friend, your OB, your midwife), it's ok to ask yourself 'but why?' three times. By the third 'why,' you'll know whether their recommendation is for you.",
        prompts: [
          "Why are you suggesting this?",
          "Why does that reason/suggestion apply to me specifically?",
          "Why would I choose this over the alternative?",
        ],
        note: "The 'why' is a kindness. A conversation and time to let your truth arise and land in your body.",
      },
      {
        label: 'Three. Your voice in labor',
        body: "You process by speaking. Tell your support people now, before labor:",
        prompts: [
          "When I'm talking through a contraction, please let me talk. Echo me gently. Stop echoing me if I signal verbally or physically that I don't want that.",
          "When I go silent, don't fill the silence. I'll come back to vocalizing on my own when I'm ready.",
          "The one question I want you to ask me if I seem stuck is _______.",
        ],
        note: "Write the question that would anchor you. That's your mantra.",
      },
    ],
    closing: "Your voice is your wisdom. Speak your truth, and you'll find your way.",
  },

  nurturer: {
    title: "A Nurturer's Circle of Care Map",
    subtitle: "A guided page to name the people who'll hold you while you hold your baby.",
    intro:
      "You show up for everyone. Now it's your turn to be held. This map helps you name the people in your circle. Who they are, what they're for, and how to reach them when you want them. Take your time to fill it in, but do fill it in. It's your turn to be held.",
    sections: [
      {
        label: 'One. The inner circle (1 to 2 people)',
        body: "These are the people who will be with you at your birth, or in the first week or so postpartum; the 2am person you can call to talk you through it. They know you well, and you don't have to explain yourself to them. Best friends and experienced mothers are great for this role.",
        prompts: [
          "Name. Relationship. What they'll do. Their phone number.",
          "___. ___. ___. ___.",
          "___. ___. ___. ___.",
        ],
        note: "Tell each person they're on this list. Your people will be touched to know you counted them in during this sacred season. They <em>want</em> to be there for you and your family.",
      },
      {
        label: 'Two. The practical circle (2 to 4 people)',
        body: "These are your lifelines for the small, everyday things. A meal, a school pickup, an extra set of hands. They don't need to know you <em>as well</em>. They just need to be reliable.",
        prompts: [
          "Meals. ___ and ___.",
          "School or older children and/or pets. ______.",
          "Errands and the house. ___.",
        ],
        note: "Ask each of them before Baby arrives. People almost always say yes when you ask for something specific.",
      },
      {
        label: 'Three. The professional circle',
        body: "These are the paid and trained people in your corner. List them in a visible place in your house (i.e. the fridge or the main entrance) so anyone helping you can find them quickly.",
        prompts: [
          "Midwife or OB. ___.",
          "Doula (if applicable). ___.",
          "IBCLC or lactation support. ___.",
          "Mental health support. ___.",
          "Out-of-hours number for concerns. ___.",
        ],
        note: "Print this map. Stick it on your fridge. You will not remember it, nor are you meant to, in the fog of the fourth trimester.",
      },
    ],
    closing: "You're worthy of being taken care of. We were <em>never</em> meant to mother alone.",
  },
};
